import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { notify } from "../lib/notify";
import DashboardLayout from "../components/DashboardLayout";
import { Plus, X, Ticket, Users, Trash2, ChevronRight } from "lucide-react";

export default function GuestListsPage() {
  const [lists, setLists] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/guest-lists").then((r) => setLists(r.data.guest_lists)).catch(() => setLists([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  return (
    <DashboardLayout
      title="Guest Lists"
      subtitle="Invite-only comp tickets that never touch your paid inventory"
      actions={<button className="st-btn" onClick={() => setOpen(true)} data-testid="new-guest-list-btn"><Plus size={14} /> New guest list</button>}
    >
      {lists === null ? (
        <div className="text-sm text-[#8a8a8a] py-10 text-center">Loading…</div>
      ) : lists.length === 0 ? (
        <div className="st-card p-10 text-center" data-testid="guest-lists-empty">
          <Ticket size={28} className="mx-auto text-[#dc2626] mb-3" />
          <h3 className="text-lg font-semibold">No guest lists yet</h3>
          <p className="text-sm text-[#8a8a8a] mt-1 max-w-md mx-auto">Create a guest list (e.g. VIP, Creators) with its own public sign-up page, its own dates, and its own guest allowances.</p>
          <button className="st-btn mt-5" onClick={() => setOpen(true)} data-testid="empty-new-guest-list-btn"><Plus size={14} /> New guest list</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="guest-lists-grid">
          {lists.map((gl) => (
            <button
              key={gl.id}
              onClick={() => nav(`/dashboard/guest-lists/${gl.id}`)}
              className="st-card p-5 text-left hover:border-[#dc2626]/50 transition-colors"
              data-testid={`guest-list-card-${gl.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-base font-semibold truncate">{gl.title}</div>
                  <div className="text-xs text-[#8a8a8a] mt-0.5">{gl.event_name}</div>
                </div>
                <span className={`st-chip ${gl.status === "active" ? "st-chip-green" : ""}`}>{gl.status}</span>
              </div>
              {gl.description && <p className="text-sm text-[#bbb] mt-2 line-clamp-2">{gl.description}</p>}
              <div className="flex items-center gap-4 mt-4 text-xs text-[#8a8a8a]">
                <span className="inline-flex items-center gap-1"><Ticket size={13} /> {gl.date_ids?.length || 0} dates</span>
                <span className="inline-flex items-center gap-1"><Users size={13} /> {gl.guests || 0} guests</span>
                <span className="ml-auto inline-flex items-center gap-1 text-[#dc2626]">Manage <ChevronRight size={13} /></span>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && <CreateModal onClose={() => setOpen(false)} onCreated={(gl) => { setOpen(false); nav(`/dashboard/guest-lists/${gl.id}`); }} />}
    </DashboardLayout>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [dates, setDates] = useState([]);
  const [selDates, setSelDates] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [maxGuests, setMaxGuests] = useState(0);
  const [fields, setFields] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!eventId) { setDates([]); setSelDates([]); return; }
    api.get(`/events/${eventId}`).then((r) => {
      const ds = (r.data.dates || []).filter((d) => d.status === "active" && !d.is_blocked);
      ds.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      setDates(ds); setSelDates([]);
    }).catch(() => setDates([]));
  }, [eventId]);

  const toggleDate = (id) => setSelDates((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const addField = () => setFields((p) => [...p, { label: "", required: false }]);
  const updField = (i, patch) => setFields((p) => p.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const rmField = (i) => setFields((p) => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!title.trim()) return notify.error("Give your guest list a title");
    if (!eventId) return notify.error("Pick an event");
    if (selDates.length === 0) return notify.error("Select at least one date");
    setBusy(true);
    try {
      const r = await api.post("/guest-lists", {
        event_id: eventId, title: title.trim(), description: description.trim(),
        date_ids: selDates, max_additional_guests: Number(maxGuests) || 0,
        custom_fields: fields.filter((f) => f.label.trim()).map((f) => ({ key: f.label, label: f.label.trim(), required: !!f.required })),
      });
      notify.success("Guest list created");
      onCreated(r.data);
    } catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto st-fade-up" data-testid="create-guest-list-modal">
      <div className="st-card p-6 w-full max-w-lg my-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">New guest list</h2>
          <button onClick={onClose} data-testid="close-create-modal"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <Field label="Title">
            <input className="st-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. VIP, Content Creators" data-testid="gl-title-input" />
          </Field>
          <Field label="Description (shown on the sign-up page)">
            <textarea className="st-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="gl-desc-input" />
          </Field>
          <Field label="Event">
            <select className="st-input" value={eventId} onChange={(e) => setEventId(e.target.value)} data-testid="gl-event-select">
              <option value="">Select an event…</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          </Field>
          {eventId && (
            <Field label={`Available dates (${selDates.length} selected)`}>
              {dates.length === 0 ? <p className="text-sm text-[#8a8a8a]">No active dates for this event.</p> : (
                <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto pr-1" data-testid="gl-dates-list">
                  {dates.map((d) => (
                    <label key={d.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${selDates.includes(d.id) ? "border-[#dc2626] bg-[#dc2626]/10" : "border-[#1a1a1a]"}`} data-testid={`gl-date-${d.id}`}>
                      <input type="checkbox" checked={selDates.includes(d.id)} onChange={() => toggleDate(d.id)} className="accent-[#dc2626]" />
                      <span>{fmtShort(d.date)}</span>
                    </label>
                  ))}
                </div>
              )}
            </Field>
          )}
          <Field label="Max additional guests each invitee can bring">
            <input type="number" min={0} max={20} className="st-input w-28" value={maxGuests} onChange={(e) => setMaxGuests(e.target.value)} data-testid="gl-maxguests-input" />
          </Field>
          <Field label="Custom fields (optional)">
            <div className="space-y-2">
              {fields.map((f, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`gl-field-row-${i}`}>
                  <input className="st-input flex-1" placeholder="Field label (e.g. Channel name)" value={f.label} onChange={(e) => updField(i, { label: e.target.value })} data-testid={`gl-field-label-${i}`} />
                  <label className="flex items-center gap-1 text-xs text-[#8a8a8a] whitespace-nowrap">
                    <input type="checkbox" checked={f.required} onChange={(e) => updField(i, { required: e.target.checked })} className="accent-[#dc2626]" /> Required
                  </label>
                  <button onClick={() => rmField(i)} className="text-[#dc2626]" data-testid={`gl-field-remove-${i}`}><Trash2 size={15} /></button>
                </div>
              ))}
              <button onClick={addField} className="st-btn st-btn-ghost text-xs" data-testid="gl-add-field-btn"><Plus size={13} /> Add field</button>
            </div>
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button className="st-btn st-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="st-btn" onClick={submit} disabled={busy} data-testid="submit-guest-list">{busy ? "Creating…" : "Create guest list"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#8a8a8a] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function fmtShort(ds) {
  if (!ds) return "";
  const d = new Date(ds + "T12:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
}
